import React from "react";
import { useApolloClient } from "@apollo/client/react";
import {
  clearStoredWorkspaceId,
  getStoredWorkspaceId,
  setStoredWorkspaceId,
} from "#app/lib/active-workspace-storage";
import {
  AcceptWorkspaceInvitationDocument,
  AccountWorkspacesDocument,
  CurrentUserProfileFragmentDoc,
  DeclineWorkspaceInvitationDocument,
  NotificationUnreadCountDocument,
  WorkspaceDataDocument,
} from "#app/graphql/generated/graphql";

import { useAuth } from "#app/lib/auth-context";
import { clearMediaObjectUrlCache } from "#app/lib/media-content";
import { resetFormSaves, settleFormSaves } from "#app/lib/forms/use-forms-store";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { connectionRows } from "./entities/types";
import { getActiveWorkspaceId, setActiveWorkspaceId } from "./data-provider";
import { restartWsConnection } from "./apollo-client";
import type {
  CurrentUser,
  ExternalAppLink,
  WorkspaceMember,
  WorkspaceStage,
  WorkspaceTeam,
  WorkspaceWorkflow as Workflow,
} from "./entities/rows";
import { at } from "#app/lib/utils/at";

export type { ExternalAppLink };

// Every shape this provider hands out is a row alias over the `WorkspaceData`
// fragments (`entities/rows.ts`). An alias cannot drift from the schema, so
// `User.name` stays nullable and `Workflow.visibility` stays required (R2, R4).
export type {
  CurrentUser,
  CurrentUserRole,
  WorkspaceDeletionRow,
  WorkspaceMember,
  WorkspaceStage,
  WorkspaceTeam,
  WorkspaceWorkflow as Workflow,
} from "./entities/rows";
import type { Omittable } from "@salestrak/ui";
import type { WorkspaceDeletionRow, WorkspaceInviterRow } from "./entities/rows";
import { AnalyticsContext, WorkspaceEvent, trackEvent } from "#app/lib/analytics";

export type AppDataWorkspace = Omittable<{
  id: string;
  workspaceId: string;
  name: string;
  urlPath: string;
  settings?: {
    logo?: string;
    timezone?: string;
  };
  // The tile's key color — free-form string from the API, narrowed at
  // WorkspaceAvatar; unset renders the default grey tile.
  avatarColor?: string | null;
  // External companion-app links surfaced in the sidebar "Apps" section.
  // Workspace-wide setting; empty (or absent) when none are configured.
  externalAppLinks?: ExternalAppLink[];
  memberStatus?: string;
  // Non-null in the schema (`Workspace.memberCount`); every workspace row
  // comes through `WorkspaceStandard_workspace`, which selects it.
  memberCount: number;
  // Set while the workspace is frozen and scheduled for purge; the shell
  // yields to the pending-deletion screen. Null while live.
  deletion?: WorkspaceDeletionRow | null;
  // Deletion is Owner-only in both directions; this decides which actions
  // the pending screen and the settings danger zone offer.
  viewerIsOwner?: boolean;
  // Who invited the viewer here and when (`Workspace.viewerInvitedBy` /
  // `viewerInvitedAt`). Both null on a row the viewer created; the invite
  // screen and the picker's invited row read them for "Invited by …".
  viewerInvitedBy?: WorkspaceInviterRow | null;
  viewerInvitedAt?: string | null;
}>;

export type Workspace = AppDataWorkspace;

export interface AppDataContextValue {
  currentUser: CurrentUser | null;
  workspaces: AppDataWorkspace[];
  workflows: Workflow[];
  teams: WorkspaceTeam[];
  members: WorkspaceMember[];
  stages: WorkspaceStage[];
  isLoading: boolean;
  error: string | null;
  /** Reload the workspace list and the active workspace's data. Resolves to
   *  the fresh list, or null when the load failed. */
  refetch: () => Promise<AppDataWorkspace[] | null>;
}

export interface WorkspaceContextValue {
  activeWorkspace: Workspace | null;
  workspaces: Workspace[];
  isLoading: boolean;
  error: string | null;
  switchWorkspace: (workspaceIdOrTarget: string | Workspace) => Promise<boolean>;
  /** Reload the account's workspace list. Resolves to the fresh list, or
   *  null when the load failed. A caller that acts on a row it just changed
   *  (an accepted invite) reads it from here: the list in this render is
   *  still the old one. */
  refreshWorkspaces: () => Promise<Workspace[] | null>;
  /** `via` names the door for `workspace.accept_invite` / `decline_invite`:
   *  "gate", "picker" or "email_link". */
  acceptInvitation: (workspaceId: string, via: string) => Promise<void>;
  declineInvitation: (workspaceId: string, via: string) => Promise<void>;
  /** The viewer no longer belongs to `workspaceId` (they left). Drops it as
   *  the active workspace — header, stored id, caches — and refreshes the
   *  account list. Resolves to the refreshed list so the caller can pick
   *  where to land next. */
  detachWorkspace: (workspaceId: string) => Promise<Workspace[]>;
}

interface AppState {
  currentUser: CurrentUser | null;
  workspaces: AppDataWorkspace[];
  workflows: Workflow[];
  teams: WorkspaceTeam[];
  members: WorkspaceMember[];
  stages: WorkspaceStage[];
  activeWorkspace: AppDataWorkspace | null;
  isLoading: boolean;
  error: string | null;
}

const INITIAL_STATE: AppState = {
  currentUser: null,
  workspaces: [],
  workflows: [],
  teams: [],
  members: [],
  stages: [],
  activeWorkspace: null,
  isLoading: true,
  error: null,
};

let nextSwitchId = 0;

type Action =
  | { type: "SET_LOADING" }
  | { type: "SET_STATE"; state: AppState }
  | { type: "SET_ERROR"; error: string }
  | { type: "SWITCH_START"; target: string | AppDataWorkspace; switchId: number }
  | { type: "SWITCH_COMPLETE"; rawData: any; switchId: number }
  | { type: "SWITCH_FAILED"; switchId: number }
  | {
      type: "REFETCH_COMPLETE";
      workspaces: AppDataWorkspace[];
      rawWsData: any;
    }
  | {
      // The viewer left `workspaceId`. When it was the active workspace every
      // workspace-scoped slice empties with it — there is no bootstrap to
      // load for a workspace that refuses the viewer.
      type: "WORKSPACE_DETACHED";
      workspaceId: string;
      workspaces: AppDataWorkspace[];
    }
  | {
      // Cache-driven update of the signed-in user's profile fields (avatar
      // save, profile edit). Keeps the provider snapshot in sync with the
      // normalized Apollo User entity between WorkspaceData fetches.
      type: "CURRENT_USER_PATCH";
      patch: Pick<CurrentUser, "name" | "username" | "email" | "profileImage">;
    }
  | { type: "LOGOUT" };

interface ReducerState extends AppState {
  switchId: number;
}

const INITIAL_REDUCER_STATE: ReducerState = { ...INITIAL_STATE, switchId: 0 };

function appReducer(state: ReducerState, action: Action): ReducerState {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, isLoading: true };

    case "SET_STATE":
      return { ...action.state, switchId: state.switchId };

    case "SET_ERROR":
      return { ...state, isLoading: false, error: action.error };

    case "SWITCH_START": {
      const target =
        typeof action.target === "string"
          ? state.workspaces.find((w) => w.workspaceId === action.target)
          : action.target;
      if (!target) return state;
      const inList = state.workspaces.some(
        (w) => w.workspaceId === target.workspaceId,
      );
      return {
        ...state,
        activeWorkspace: target,
        workspaces: inList
          ? state.workspaces
          : [...state.workspaces, target],
        isLoading: true,
        switchId: action.switchId,
      };
    }

    case "SWITCH_COMPLETE": {
      if (action.switchId !== state.switchId) return state;
      const wsData = parseWorkspaceData(action.rawData);
      return {
        ...state,
        currentUser: wsData.currentUser,
        workflows: wsData.workflows,
        teams: wsData.teams,
        members: wsData.members,
        stages: wsData.stages,
        isLoading: false,
        error: null,
      };
    }

    case "SWITCH_FAILED":
      if (action.switchId !== state.switchId) return state;
      return { ...state, isLoading: false };

    case "REFETCH_COMPLETE": {
      const wsData = parseWorkspaceData(action.rawWsData);
      const active = state.activeWorkspace
        ? (action.workspaces.find(
            (w) => w.workspaceId === state.activeWorkspace!.workspaceId,
          ) ?? null)
        : null;
      return {
        ...state,
        workspaces: action.workspaces,
        currentUser: wsData.currentUser,
        workflows: wsData.workflows,
        teams: wsData.teams,
        members: wsData.members,
        stages: wsData.stages,
        activeWorkspace: active,
      };
    }

    case "WORKSPACE_DETACHED": {
      if (state.activeWorkspace?.workspaceId !== action.workspaceId) {
        return { ...state, workspaces: action.workspaces };
      }
      return {
        ...state,
        workspaces: action.workspaces,
        activeWorkspace: null,
        currentUser: null,
        workflows: [],
        teams: [],
        members: [],
        stages: [],
        isLoading: false,
        error: null,
      };
    }

    case "CURRENT_USER_PATCH": {
      const current = state.currentUser;
      if (!current) return state;
      const { patch } = action;
      const unchanged =
        current.name === patch.name &&
        current.username === patch.username &&
        current.email === patch.email &&
        current.profileImage === patch.profileImage;
      if (unchanged) return state;
      return {
        ...state,
        currentUser: { ...current, ...patch },
        // The signed-in user also appears in the members pool (entity
        // selectors, assignee lists) — keep that row consistent.
        members: state.members.map((m) =>
          m.userId === current.userId ? { ...m, ...patch } : m,
        ),
      };
    }

    case "LOGOUT":
      return INITIAL_REDUCER_STATE;
  }
}

function parseWorkspaces(workspacesData: any): AppDataWorkspace[] {
  return connectionRows<AppDataWorkspace>(workspacesData?.workspaces);
}

function parseWorkspaceData(data: any) {
  return {
    currentUser: (data?.me as CurrentUser) ?? null,
    workflows: connectionRows<Workflow>(data?.workflows),
    teams: connectionRows<WorkspaceTeam>(data?.teams),
    members: connectionRows<WorkspaceMember>(data?.users),
    stages: connectionRows<WorkspaceStage>(data?.stages),
  };
}

function selectWorkspace(
  workspaces: AppDataWorkspace[],
  targetId: string | null,
): AppDataWorkspace | null {
  if (targetId) {
    const match = workspaces.find(
      (w) => w.workspaceId === targetId && w.memberStatus === "active",
    );
    if (match) return match;
  }
  const activeWorkspaces = workspaces.filter(
    (w) => w.memberStatus === "active",
  );
  if (activeWorkspaces.length === 1) return at(activeWorkspaces, 0, "workspace");
  return null;
}

async function activateWorkspace(workspaceId: string) {
  await setStoredWorkspaceId(workspaceId);
  setActiveWorkspaceId(workspaceId);
}

interface AppContextValue extends AppState {
  refetch: () => Promise<AppDataWorkspace[] | null>;
  switchWorkspace: (workspaceIdOrTarget: string | AppDataWorkspace) => Promise<boolean>;
  refreshWorkspaces: () => Promise<AppDataWorkspace[] | null>;
  /** `via` names the door for `workspace.accept_invite` / `decline_invite`:
   *  "gate", "picker" or "email_link". */
  acceptInvitation: (workspaceId: string, via: string) => Promise<void>;
  declineInvitation: (workspaceId: string, via: string) => Promise<void>;
  detachWorkspace: (workspaceId: string) => Promise<AppDataWorkspace[]>;
}

const AppContext = React.createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const client = useApolloClient();
  const { state: authState, impersonation } = useAuth();
  const impersonatedWorkspaceId = impersonation?.workspace.id ?? null;
  const authResolved = authState.isAuthenticated !== null;
  const isAuthenticated = authState.isAuthenticated === true;

  const [state, dispatch] = React.useReducer(appReducer, INITIAL_REDUCER_STATE);

  // The provider snapshots WorkspaceData into reducer state, so cache writes
  // from mutations (setUserAvatar, updateUser) wouldn't reach consumers like
  // the header profile button on their own. Watch the signed-in user's
  // normalized cache entry and patch the snapshot whenever it changes.
  const currentUserId = state.currentUser?.userId ?? null;
  React.useEffect(() => {
    if (!currentUserId) return;
    const subscription = client
      .watchFragment({
        fragment: CurrentUserProfileFragmentDoc,
        from: { __typename: "User", userId: currentUserId },
      })
      .subscribe((result) => {
        if (!result.complete) return;
        dispatch({
          type: "CURRENT_USER_PATCH",
          patch: {
            name: result.data.name ?? "",
            username: result.data.username ?? "",
            email: result.data.email ?? "",
            profileImage: result.data.profileImage ?? null,
          },
        });
      });
    return () => subscription.unsubscribe();
  }, [client, currentUserId]);

  const fetchWorkspaces = React.useCallback(async () => {
    // Workspace list is the only thing we can fetch before an active
    // workspace is selected — `currentUser` comes from WorkspaceData.me
    // once we're scoped to one.
    const result = await client.query({
      query: AccountWorkspacesDocument,
      fetchPolicy: "network-only",
    });
    // The client-wide `errorPolicy: "all"` resolves instead of rejecting on a
    // failure, so an unreachable API would otherwise read as an EMPTY account
    // ("Create a Workspace to get started") rather than a network error.
    if (result.error) throw result.error;
    const workspaces = parseWorkspaces(result.data);
    // The API pins an impersonation session to one workspace, so offering the
    // rest of the member's workspaces would only lead to refused requests.
    return impersonatedWorkspaceId
      ? workspaces.filter((w) => w.id === impersonatedWorkspaceId)
      : workspaces;
  }, [client, impersonatedWorkspaceId]);

  const fetchWorkspaceData = React.useCallback(async (policy: "network-only" | "cache-first" = "network-only") => {
    // Both WorkspaceData and the notification badge count are on apiv2.
    // Firing them in parallel keeps the Apollo cache hydrated for
    // useInboxUnreadCount's cache-only read.
    const [wsResult] = await Promise.all([
      client.query({ query: WorkspaceDataDocument, fetchPolicy: policy }),
      client
        .query({ query: NotificationUnreadCountDocument, fetchPolicy: policy })
        .catch(() => null),
    ]);
    // `errorPolicy: "all"` resolves failures; throw so callers' catch paths
    // (init's tolerated-partial branch, switchWorkspace's toast) actually run
    // instead of parsing `undefined` into an empty workspace.
    if (wsResult.error) throw wsResult.error;
    return wsResult.data;
  }, [client]);

  // The account-level list decides whether the workspace bootstrap runs: a
  // frozen (pending-deletion) workspace refuses it, and the refetch after
  // scheduling one is exactly when that state first appears here.
  // The boot load below, while it runs. A refetch waits for it: the boot
  // request can start before a change (an invite accepted the moment sign-in
  // completes) and answer after the refetch, and its older list would then
  // overwrite the fresh one.
  const bootLoad = React.useRef<Promise<void> | null>(null);

  const refetch = React.useCallback(async (): Promise<AppDataWorkspace[] | null> => {
    await bootLoad.current;
    try {
      const workspaces = await fetchWorkspaces();
      const activeId = getActiveWorkspaceId();
      const active = workspaces.find((w) => w.workspaceId === activeId);
      // The bootstrap is workspace-scoped: with no active workspace (an
      // invitee who has just accepted, before the app selects one) the
      // request fails, and the fresh list would be lost with it.
      const rawWsData = active && !active.deletion ? await fetchWorkspaceData() : null;
      dispatch({ type: "REFETCH_COMPLETE", workspaces, rawWsData });
      return workspaces;
    } catch (err) {
      console.error("[AppProvider] Refetch failed:", err);
      return null;
    }
  }, [fetchWorkspaces, fetchWorkspaceData]);

  React.useEffect(() => {
    if (!authResolved) return;

    if (!isAuthenticated) {
      setActiveWorkspaceId(null);
      void clearStoredWorkspaceId();
      dispatch({ type: "LOGOUT" });
      return;
    }

    let cancelled = false;
    dispatch({ type: "SET_LOADING" });

    const boot = (async () => {
      try {
        const workspaces = await fetchWorkspaces();
        if (cancelled) return;

        const storedId = await getStoredWorkspaceId();
        const active = selectWorkspace(workspaces, storedId);

        if (active) {
          await activateWorkspace(active.workspaceId);

          // A frozen workspace refuses its bootstrap (WorkspaceMemberGuard);
          // the pending-deletion screen needs none of it, so skip the request
          // rather than let its rejection toast on boot.
          let wsData: ReturnType<typeof parseWorkspaceData> | null = null;
          if (!active.deletion) {
            try {
              const wsRawData = await fetchWorkspaceData("network-only");
              wsData = parseWorkspaceData(wsRawData);
            } catch {}
          }

          if (cancelled) return;

          dispatch({
            type: "SET_STATE",
            state: {
              currentUser: wsData?.currentUser ?? null,
              workspaces,
              workflows: wsData?.workflows ?? [],
              teams: wsData?.teams ?? [],
              members: wsData?.members ?? [],
              stages: wsData?.stages ?? [],
              activeWorkspace: active,
              isLoading: false,
              error: null,
            },
          });
        } else {
          if (cancelled) return;

          dispatch({
            type: "SET_STATE",
            state: {
              currentUser: null,
              workspaces,
              workflows: [],
              teams: [],
              members: [],
              stages: [],
              activeWorkspace: null,
              isLoading: false,
              error: null,
            },
          });
        }
      } catch (err) {
        if (cancelled) return;
        console.error("[AppProvider] Init failed:", err);
        dispatch({
          type: "SET_ERROR",
          error: "Failed to load account data.",
        });
      }
    })();
    bootLoad.current = boot;
    void boot.finally(() => {
      if (bootLoad.current === boot) bootLoad.current = null;
    });

    return () => {
      cancelled = true;
    };
  }, [authResolved, isAuthenticated, fetchWorkspaces, fetchWorkspaceData]);

  const switchWorkspace = React.useCallback(
    async (workspaceIdOrTarget: string | AppDataWorkspace): Promise<boolean> => {
      const target =
        typeof workspaceIdOrTarget === "string"
          ? state.workspaces.find((w) => w.workspaceId === workspaceIdOrTarget)
          : workspaceIdOrTarget;
      if (!target) return false;

      const workspaceId = target.workspaceId;
      const switchId = ++nextSwitchId;
      dispatch({ type: "SWITCH_START", target, switchId });

      await settleFormSaves();
      await client.clearStore();
      // Photo blobs are cached by id only, with no workspace scoping — drop
      // them so a gated photo from the previous workspace can't render in the
      // new one if ids ever collide.
      clearMediaObjectUrlCache();
      restartWsConnection();
      await activateWorkspace(workspaceId);

      try {
        // A frozen target has no bootstrap to load — its screen needs none.
        const rawData = target.deletion
          ? null
          : await fetchWorkspaceData("network-only");
        dispatch({ type: "SWITCH_COMPLETE", rawData, switchId });
        return true;
      } catch (err) {
        if (switchId === nextSwitchId) {
          toastMutationError(err, "Failed to load workspace");
        }
        dispatch({ type: "SWITCH_FAILED", switchId });
        return false;
      }
    },
    [client, fetchWorkspaceData, state.workspaces],
  );

  // `refetch` catches its own failures and resolves null for them.
  const refreshWorkspaces = React.useCallback(() => refetch(), [refetch]);

  const acceptInvitation = React.useCallback(
    async (workspaceId: string, via: string) => {
      try {
        await client.mutate({
          mutation: AcceptWorkspaceInvitationDocument,
          variables: { workspaceId },
        });
        trackEvent({
          context: AnalyticsContext.Workspace,
          event: WorkspaceEvent.AcceptInvite,
          data: { via },
        });
        await refetch();
        await switchWorkspace(workspaceId);
      } catch (err) {
        toastMutationError(err, "Failed to accept invitation");
      }
    },
    [client, refetch, switchWorkspace],
  );

  const declineInvitation = React.useCallback(
    async (workspaceId: string, via: string) => {
      try {
        await client.mutate({
          mutation: DeclineWorkspaceInvitationDocument,
          variables: { workspaceId },
        });
        trackEvent({
          context: AnalyticsContext.Workspace,
          event: WorkspaceEvent.DeclineInvite,
          data: { via },
        });
        await refetch();
      } catch (err) {
        toastMutationError(err, "Failed to decline invitation");
      }
    },
    [client, refetch],
  );

  const detachWorkspace = React.useCallback(
    async (workspaceId: string): Promise<AppDataWorkspace[]> => {
      if (getActiveWorkspaceId() === workspaceId) {
        // Same teardown as a switch: the header stops naming a workspace
        // that now refuses the viewer, and nothing cached under it may
        // paint again.
        resetFormSaves();
        setActiveWorkspaceId(null);
        await clearStoredWorkspaceId();
        await client.clearStore();
        clearMediaObjectUrlCache();
        restartWsConnection();
      }
      const workspaces = await fetchWorkspaces();
      dispatch({ type: "WORKSPACE_DETACHED", workspaceId, workspaces });
      return workspaces;
    },
    [client, fetchWorkspaces],
  );

  const value = React.useMemo<AppContextValue>(() => {
    const { switchId: _, ...appState } = state;
    return {
      ...appState,
      refetch,
      switchWorkspace,
      refreshWorkspaces,
      acceptInvitation,
      declineInvitation,
      detachWorkspace,
    };
  }, [state, refetch, switchWorkspace, refreshWorkspaces, acceptInvitation, declineInvitation, detachWorkspace]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

function useAppContext(): AppContextValue {
  const ctx = React.useContext(AppContext);
  if (!ctx) {
    throw new Error("useAppData/useWorkspace must be used within AppProvider");
  }
  return ctx;
}

export function useAppData(): AppDataContextValue {
  const ctx = useAppContext();
  return React.useMemo(
    () => ({
      currentUser: ctx.currentUser,
      workspaces: ctx.workspaces,
      workflows: ctx.workflows,
      teams: ctx.teams,
      members: ctx.members,
      stages: ctx.stages,
      isLoading: ctx.isLoading,
      error: ctx.error,
      refetch: ctx.refetch,
    }),
    [
      ctx.currentUser,
      ctx.workspaces,
      ctx.workflows,
      ctx.teams,
      ctx.members,
      ctx.stages,
      ctx.isLoading,
      ctx.error,
      ctx.refetch,
    ],
  );
}

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useAppContext();
  return React.useMemo(
    () => ({
      activeWorkspace: ctx.activeWorkspace,
      workspaces: ctx.workspaces,
      isLoading: ctx.isLoading,
      error: ctx.error,
      switchWorkspace: ctx.switchWorkspace,
      refreshWorkspaces: ctx.refreshWorkspaces,
      acceptInvitation: ctx.acceptInvitation,
      declineInvitation: ctx.declineInvitation,
      detachWorkspace: ctx.detachWorkspace,
    }),
    [
      ctx.activeWorkspace,
      ctx.workspaces,
      ctx.isLoading,
      ctx.error,
      ctx.switchWorkspace,
      ctx.refreshWorkspaces,
      ctx.acceptInvitation,
      ctx.declineInvitation,
      ctx.detachWorkspace,
    ],
  );
}
